import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { IClub, IEvent, ICourse } from "@eagerdog/interfaces";
import Cropper, { Area } from 'react-easy-crop';

import { apiService } from "src/services";
import { helperService } from "src/services/helper.service";
import { localStorageService } from "src/services/local-storage.service";

import TabContent, { ITab, useTabContent } from "src/components/TabContent/TabContent";
import Button from "src/components/Button/Button";
import Icon from "src/components/Icon/Icon";
import { Modal, useModal } from "src/components/Modal/Modal";

import ScheduleTab from "./components/ScheduleTab/ScheduleTab";
import StoreTab from "./components/StoreTab/StoreTab";
import Feed from "src/routes/Discover/Feed/Feed";
import QuickActions from "src/routes/Discover/QuickActions/QuickActions";
import EventForm from "src/components/StepForm/EventForm/EventForm";
import CreateCourse from "src/routes/Courses/CreateCourse/CreateCourse";
import { toast } from "src/components/Toast/ToastManager";
import Congrats from "src/components/Congrats/Congrats";

import ShareModal from "src/components/ShareModal/ShareModal";
import FavoriteClubModal from "src/components/FavoriteClubModal/FavoriteClubModal";
import { DonationModal } from "./components/DonationModal";

import styles from "./KennelClub.module.scss";

interface IProps {

}

const KennelClub: React.FC<IProps> = (props) => {
  const { clubId, tab } = useParams();
  const { isShown, toggle } = useModal();
  const { isShown: showDonationModal, toggle: toggleDonationModal } = useModal();
  const navigate = useNavigate();
  const location = useLocation();

  const [loaded, setLoaded]= useState<boolean>(false);
  const user = helperService.getUser();
  const [favorites, setFavorites] = useState<string[]>(user.favorite_clubs || []);
  const [hasAdmin, setHasAdmin] = useState<boolean>(false);

  const [club, setClub] = useState<IClub>();
  
  // Image upload state
  const [selectedLogoFile, setSelectedLogoFile] = useState<string | null>(null);
  const [selectedCoverFile, setSelectedCoverFile] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement>(null);
  const coverFileInputRef = useRef<HTMLInputElement>(null);
  
  // Favorite club modal state
  const [showFavoriteModal, setShowFavoriteModal] = useState<boolean>(false);

  const { activeTab, setTab } = useTabContent();
  const [readMore, setReadMore] = useState<boolean>(false);

  // Activity leads, matching the dog profile; /k/:clubId/calendar and
  // /k/:clubId/store still select their tabs by name.
  const [tabs, setTabs] = useState<ITab[]>([
    { name: "Activity", actions: [] },
    { name: "Calendar", actions: [] }
  ]);
  // Bumped after a manager posts from the Activity tab so the feed reloads.
  const [feedRefreshKey, setFeedRefreshKey] = useState(0);

  const [modalContent, setModalContent] = useState<string>("");
  const [newEventUrl, setNewEventUrl] = useState<string>("");

  const isAdmin = (id?: string) => {
    if ((club && club.created_by ? club.created_by : id) === user._id) {
      return true;
    }

    // Check if user has admin capacity for this club
    if (club?._id && helperService.hasAdmin(club._id)) {
      return true;
    }

    return false;
  }

  // Update tabs when admin status changes
  useEffect(() => {
    setTabs([
      { name: "Activity", actions: [] },
      { name: "Calendar", actions: [] },
      { name: "Store", actions: [] }
    ]);
  }, [club]);

  // Set active tab based on URL parameter
  useEffect(() => {
    if (loaded && tab && tabs.length > 0) {
      const tabIndex = tabs.findIndex(t => t.name.toLowerCase() === tab.toLowerCase());
      if (tabIndex >= 0) {
        setTab(tabIndex);
      }
    }
  }, [tab, loaded, tabs, setTab]);

  const clubIsFavourite = () => {
    return (club?._id && favorites.includes(club?._id));
  }

  // Handle new event creation redirect (show congratulations modal)
  const searchParams = new URLSearchParams(location.search);
  const newEvent = searchParams.get("e");

  useEffect(() => {
    if (newEvent !== null) {
      setNewEventUrl(newEvent);
      setModalContent("congratsModal");
      // Clear the query parameter from URL
      navigate(location.pathname, { replace: true });
    } else {
      //
    }
  }, [newEvent, navigate, location.pathname]);

  const getModalContent = () => {
    switch (modalContent) {
      case "congratsModal":
        return <Congrats 
          text="You made an event! Now let's create some shows for your event." 
          url={`/e/${newEventUrl}/?s=1&admin=${clubId}`} 
          urlText="Create Shows" 
        />;
      case "eventForm":
        return <EventForm
          clubNumber={club?.club_number as string | undefined}
          clubId={clubId} 
          onFinish={(e: IEvent) => {
            navigate(`${location.pathname}?e=${e.handle}`, { replace: true });
            getClub()
            toggle();
          }} 
        />;
      case "createCourse":
        return <CreateCourse
          clubId={clubId as string}
          onSubmit={(c: ICourse) => { 
            toast.show({
              title: "Create Course",
              content: "Course Successfully Created",
              duration: 5000,
              type: "success"
            });
            navigate("/c/" + c.handle);
            toggle(); 
          }} 
        />;
      default:
        return <ShareModal
          eventTitle={club?.club_name || ""}
          eventUrl={window.location.href}
          onClose={() => { toggle(); }}
        />;
    }
  }

  const getClub = () => {
    apiService.getClub(clubId as string).then((c) => {
      setClub(c);
      setLoaded(true);
      localStorageService.addRecentActivity({
        type: "club",
        id: c._id,
        name: c.club_name,
        link: `/k/${c._id}`,
        logo_url: c.logo_url,
      });
      
      if (isAdmin(c.created_by) || (!isAdmin(c.created_by) && helperService.hasAdmin(c._id))) {
        setHasAdmin(true);
      }
    });
  }

  const uploadImage = async (file: File, type: 'logo' | 'cover'): Promise<string> => {
    try {
      const response = await apiService.uploadFile(file);
      
      if (typeof response === 'string') {
        return response;
      } else if (response && typeof response === 'object' && 'value' in response) {
        return (response as any).value;
      } else {
        throw new Error('Upload response did not contain a file URL');
      }
    } catch (error) {
      console.error(`Failed to upload ${type} image:`, error);
      throw error;
    }
  };

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = useCallback(
    async (imageSrc: string, pixelCrop: Area, isLogo: boolean): Promise<File> => {
      const createImage = (url: string): Promise<HTMLImageElement> =>
        new Promise((resolve, reject) => {
          const image = new Image();
          image.addEventListener('load', () => resolve(image));
          image.addEventListener('error', (error) => reject(error));
          image.crossOrigin = 'anonymous';
          image.src = url;
        });

      const image = await createImage(imageSrc);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('No 2d context');
      }

      if (isLogo) {
        // Logo: square output
        const maxWidth = 200;
        const maxHeight = 200;
        let outputWidth = pixelCrop.width;
        let outputHeight = pixelCrop.height;
        
        if (outputWidth > maxWidth || outputHeight > maxHeight) {
          const scale = Math.min(maxWidth / outputWidth, maxHeight / outputHeight);
          outputWidth = outputWidth * scale;
          outputHeight = outputHeight * scale;
        }
        
        const pixelRatio = window.devicePixelRatio;
        canvas.width = outputWidth * pixelRatio;
        canvas.height = outputHeight * pixelRatio;

        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          outputWidth,
          outputHeight
        );
      } else {
        // Cover: true size
        canvas.width = Math.floor(pixelCrop.width);
        canvas.height = Math.floor(pixelCrop.height);
        
        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );
      }

      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            const file = new File([blob], 'cropped-image.jpg', {
              type: 'image/jpeg',
            });
            resolve(file);
          },
          'image/jpeg',
          0.9
        );
      });
    },
    []
  );

  const handleSaveLogo = async () => {
    if (selectedLogoFile && croppedAreaPixels) {
      try {
        const croppedImageFile = await createCroppedImage(selectedLogoFile, croppedAreaPixels, true);
        await handleLogoChange(croppedImageFile);
      } catch (e) {
        console.error('Error cropping image:', e);
        setSelectedLogoFile(null);
      }
    }
  };

  const handleSaveCover = async () => {
    if (selectedCoverFile && croppedAreaPixels) {
      try {
        const croppedImageFile = await createCroppedImage(selectedCoverFile, croppedAreaPixels, false);
        await handleCoverImageChange(croppedImageFile);
      } catch (e) {
        console.error('Error cropping image:', e);
        setSelectedCoverFile(null);
      }
    }
  };

  const handleLogoChange = async (file: File | null) => {
    if (file && club) {
      try {
        const logoUrl = await uploadImage(file, 'logo');
        
        const updatedClub = await apiService.updateClub(clubId as string, { logo_url: logoUrl });
        setClub(updatedClub);
        setSelectedLogoFile(null); // Close the cropper
        
        toast.show({
          title: "Club Logo",
          content: "Club logo updated successfully",
          duration: 3000,
          type: "success"
        });
      } catch (e: any) {
        setSelectedLogoFile(null); // Close on error
        toast.show({
          title: "Club Logo",
          content: "Failed to update club logo",
          duration: 5000,
          errorDetails: e,
          type: "fail"
        });
      }
    } else {
      setSelectedLogoFile(null); // Close if cancelled
    }
  };

  const handleCoverImageChange = async (file: File | null) => {
    if (file && club) {
      try {
        const coverUrl = await uploadImage(file, 'cover');
        
        const updatedClub = await apiService.updateClub(clubId as string, { cover_image_url: coverUrl });
        setClub(updatedClub);
        setSelectedCoverFile(null); // Close the cropper
        
        toast.show({
          title: "Club Cover",
          content: "Club cover image updated successfully",
          duration: 3000,
          type: "success"
        });
      } catch (e: any) {
        setSelectedCoverFile(null); // Close on error
        toast.show({
          title: "Club Cover",
          content: "Failed to update club cover image",
          duration: 5000,
          errorDetails: e,
          type: "fail"
        });
      }
    } else {
      setSelectedCoverFile(null); // Close if cancelled
    }
  };

  const handleLogoClick = () => {
    if (hasAdmin && logoFileInputRef.current) {
      logoFileInputRef.current.click();
    }
  };

  const handleCoverClick = () => {
    if (hasAdmin && coverFileInputRef.current) {
      coverFileInputRef.current.click();
    }
  };

  const onLogoFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSelectedLogoFile(reader.result?.toString() || '');
      });
      reader.readAsDataURL(file);
    }
  };

  const onCoverFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSelectedCoverFile(reader.result?.toString() || '');
      });
      reader.readAsDataURL(file);
    }
  };

  // Follower count shown in the Follow button; nudged locally so it tracks the viewer's own toggle.
  const bumpFollowerCount = (delta: number) => {
    setClub((c) => c ? { ...c, follower_count: Math.max(0, (c.follower_count || 0) + delta) } : c);
  };

  const toggleFavorite = (id: string, _favorites: string[]) => {
    if (!helperService.isLoggedIn()) {
      navigate(`/login?r=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }

    let newFavs = JSON.parse(JSON.stringify(_favorites));

    if (newFavs.includes(id) && newFavs.indexOf(id) !== -1) {
      // Removing from favorites - just remove, no modal
      newFavs.splice(newFavs.indexOf(id), 1);
      apiService.updateSelf({ favorite_clubs: newFavs }).then((response) => {
        localStorageService.setItem(localStorageService.USER, JSON.stringify(response));
      });
      setFavorites(newFavs);
      bumpFollowerCount(-1);
    } else {
      // Adding to favorites - add first, then show modal
      newFavs = [...newFavs, id];
      apiService.updateSelf({ favorite_clubs: newFavs }).then((response) => {
        localStorageService.setItem(localStorageService.USER, JSON.stringify(response));
      });
      setFavorites(newFavs);
      bumpFollowerCount(1);
      
      // Show the favorite modal
      setShowFavoriteModal(true);
    }
  }

  const pModalContent = helperService.usePrevious(modalContent);

  useEffect(() => {
    if (pModalContent !== modalContent) {
      if (modalContent !== "") {
        toggle();
      }
    } else {
      if (!isShown) {
        setModalContent("");
      }
    }
  }, [isShown, modalContent, pModalContent, toggle]);

  useEffect(() => {
    if (!loaded) {
      getClub();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  useEffect(() => {
    setLoaded(false);
  }, [clubId]);

  return (
    <div className={styles.KennelClub}>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{club?.club_name ? `${club.club_name} - Dog Shows & Events | Eager Dog` : "Kennel Club - Dog Shows & Events | Eager Dog"}</title>
        <meta name="description" content={club?.club_name 
          ? `Discover upcoming dog shows and events hosted by ${club.club_name}. Browse event schedules, register for competitions, and connect with fellow dog enthusiasts. ${club.address?.city ? `Located in ${club.address.city}, ${club.address.state}` : ''}.`
          : "Explore dog shows and events from kennel clubs. Find competitions, register for events, and connect with the dog community."
        } />
        <meta name="keywords" content={`${club?.club_name || 'kennel club'}, dog shows, dog events, pet competitions, ${club?.address?.city || ''}, ${club?.address?.state || ''}, dog training, canine sports, breed shows`} />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Eager Dog" />
        <link rel="canonical" href={`${window.location.origin}/k/${clubId}`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={club?.club_name ? `${club.club_name} - Dog Shows & Events` : "Kennel Club - Dog Shows & Events"} />
        <meta property="og:description" content={club?.club_name 
          ? `Join ${club.club_name} for exciting dog shows and events. ${club.address?.city ? `Located in ${club.address.city}, ${club.address.state}.` : ''}`
          : "Discover dog shows and events from kennel clubs nationwide."
        } />
        <meta property="og:url" content={`${window.location.origin}/k/${clubId}`} />
        <meta property="og:image" content={club?.cover_image_url || club?.logo_url || `${window.location.origin}/socialshare.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={club?.club_name ? `${club.club_name} kennel club page` : "Kennel club page"} />
        <meta property="og:site_name" content="Eager Dog" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@EagerDog" />
        <meta name="twitter:title" content={club?.club_name ? `${club.club_name} - Dog Shows & Events` : "Kennel Club - Dog Shows & Events"} />
        <meta name="twitter:description" content={club?.club_name 
          ? `Join ${club.club_name} for exciting dog shows and events. ${club.address?.city ? `Located in ${club.address.city}, ${club.address.state}.` : ''}`
          : "Discover dog shows and events from kennel clubs."
        } />
        <meta name="twitter:image" content={club?.cover_image_url || club?.logo_url || `${window.location.origin}/socialshare.png`} />
        <meta name="twitter:image:alt" content={club?.club_name ? `${club.club_name} kennel club` : "Kennel club"} />
        
        {/* Additional SEO Meta Tags */}
        <meta name="theme-color" content="#ea580c" />
        <meta name="apple-mobile-web-app-title" content="Eager Dog" />
        <meta name="application-name" content="Eager Dog" />
        <meta name="msapplication-TileColor" content="#ea580c" />
        
        {/* Structured Data - Organization */}
        {club && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": club.club_name,
              "url": `${window.location.origin}/k/${clubId}`,
              "logo": club.logo_url || `${window.location.origin}/logo.png`,
              "image": club.cover_image_url || club.logo_url,
              "description": club.description || `${club.club_name} is a kennel club hosting dog shows and events.`,
              "email": club.email,
              "sameAs": club.website ? [helperService.normalizeUrl(club.website)].filter(Boolean) : [],
              ...(club.address && {
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": `${club.address.line1 || ''} ${club.address.line2 || ''}`.trim(),
                  "addressLocality": club.address.city,
                  "addressRegion": club.address.state,
                  "postalCode": club.address.postal_code,
                  "addressCountry": club.address.country || "US"
                }
              })
            })}
          </script>
        )}
      </Helmet>
      {club && <div className={styles.inner}>

        {showDonationModal &&
          <DonationModal
            club={club}
            toggle={toggleDonationModal}
          />
        }

        <div className={styles.header}>
          <div 
            className={club.cover_image_url ? `${styles.headerImage} ${styles.hasImage}` : `${styles.headerImage}`}
            onClick={handleCoverClick}
            style={{ cursor: hasAdmin ? 'pointer' : 'default' }}
          >
            {club.cover_image_url && <img src={club.cover_image_url} alt={club.club_name+" Cover"} loading="lazy" />}
            {hasAdmin && <div className={styles.imageHoverOverlay}>
              <div className={styles.cameraIcon}></div>
              <span>Change Cover</span>
            </div>}
          </div>
          <div className={styles.content}>
            <div 
              className={styles.profileImage}
              onClick={handleLogoClick}
              style={{ cursor: hasAdmin ? 'pointer' : 'default' }}
            >
              {club.logo_url && <img src={club.logo_url} alt={club.club_name+" Logo"} loading="lazy" />}
              {!club.logo_url && <div className={styles.initial}>{club.club_name.substring(0, 1)}</div>}
              {hasAdmin && <div className={styles.imageHoverOverlay}>
                <div className={styles.cameraIcon}></div>
                <span>Change Logo</span>
              </div>}
            </div>
            <div className={styles.actions}>
              {club.enable_donations !== false && (user.user_type === "owner" || !user._id) && <Button onClick={() => { toggleDonationModal(); }}>Donate</Button>}
              {hasAdmin && <>
                <Link className={styles.manage} to={"/k/" + clubId + "/manage"}>Manage Club</Link>
              </>}
              <Button className={styles.iconButton} onClick={() => { toggle(); }}><Icon className={styles.buttonIcon} type="share" /><span className={styles.buttonName}>Share</span></Button>
              {!hasAdmin && (
                <button
                  type="button"
                  className={`${styles.followButton} ${clubIsFavourite() ? styles.following : ""}`}
                  aria-pressed={!!clubIsFavourite()}
                  aria-label={`${clubIsFavourite() ? "Unfollow" : "Follow"} ${club.club_name}`}
                  onClick={() => { toggleFavorite(club._id, favorites); }}
                >
                  <Icon className={styles.followIcon} type={clubIsFavourite() ? "heartFilled" : "heart"} />
                  <span className={styles.followLabel}>{clubIsFavourite() ? "Following" : "Follow"}</span>
                </button>
              )}
            </div>
            <div className={styles.title}>
              <div className={styles.left}>
                <h1 className={styles.name}>{club.club_name}</h1>
                {club.website && <div className={styles.website}><a target="_blank" rel="noreferrer" href={helperService.normalizeUrl(club.website)}>{club.website}</a></div>}
                <div className={styles.followerChip} aria-live="polite">
                  <Icon className={styles.followerChipIcon} type="heartFilled" />
                  <strong>{(club.follower_count || 0).toLocaleString()}</strong>
                  <span>{club.follower_count === 1 ? "person follows this club" : "people follow this club"}</span>
                </div>
              </div>
              <div className={styles.right}>
                <div className={styles.location}>{helperService.formatAddress(club.address)}</div>
                <div className={styles.email}><a href={"mailto:"+club.email}>{club.email}</a></div>
                <div className={styles.socialMedia}>

                </div>
              </div>
            </div>
            {/*<div className={styles.metrics}>
              <div className={styles.metric}>
                <div className={styles.metricTitle}>Followers</div>
                <div className={styles.amount}>122</div>
              </div>
              <div className={styles.metric}>
                <div className={styles.metricTitle}>Events</div>
                <div className={styles.amount}>12</div>
              </div>
              <div className={styles.metric}>
                <div className={styles.metricTitle}>Courses</div>
                <div className={styles.amount}>24</div>
              </div>
            </div>*/}
            {club.description && <div className={styles.about}>
              <div id="club-description" className={readMore ? `${styles.aboutWrap}` : `${styles.aboutWrap} ${styles.hide}`}>
                <div className={styles.descriptionWrap} dangerouslySetInnerHTML={{ __html: club.description }}></div>
              </div>
              <button
                type="button"
                className={`${styles.readMore} ${readMore ? styles.expanded : ""}`}
                aria-expanded={readMore}
                aria-controls="club-description"
                onClick={() => { setReadMore(!readMore); }}
              >
                <span>{readMore ? "Read less" : "Read more"}</span>
                <Icon className={styles.readMoreIcon} type="caretDown" />
              </button>
            </div>}
          </div>
        </div>
        <div className={styles.tabs}>
          <TabContent
            className={styles.tabs}
            setTab={(index: number) => {
              setTab(index);
              const tabName = tabs[index].name.toLowerCase();
              window.history.pushState("", "", `/k/${clubId}/${tabName}`);
            }}
            activeTab={activeTab}
            defaultTab={0}
            tabs={tabs}
          >
            {/* Always rendered so tab indexes stay aligned while the club loads. */}
            <div className={styles.activityTab}>
              {club && hasAdmin && <QuickActions club={club} onPosted={() => setFeedRefreshKey((k) => k + 1)} />}
              {club && <Feed
                key={feedRefreshKey}
                source={{ kind: "club", clubId: club._id, isManager: hasAdmin, clubName: club.club_name }}
              />}
            </div>
            {clubId && <ScheduleTab
              club={club}
              isAdmin={hasAdmin}
              onEventCreate={getClub}
            />}
            {clubId && club && <StoreTab
              club={club}
              isAdmin={hasAdmin}
            />}
          </TabContent>
        </div>
      </div>}
      <Modal
        className={modalContent || "shareModal"}
        isShown={isShown}
        hide={toggle}
        modalContent={getModalContent()}
      />
      <FavoriteClubModal
        isShown={showFavoriteModal}
        hide={() => setShowFavoriteModal(false)}
        club={club || null}
      />
      
      {/* Hidden file inputs */}
      <input
        ref={logoFileInputRef}
        type="file"
        accept="image/*"
        onChange={onLogoFileSelect}
        style={{ display: 'none' }}
      />
      <input
        ref={coverFileInputRef}
        type="file"
        accept="image/*"
        onChange={onCoverFileSelect}
        style={{ display: 'none' }}
      />
      
      {/* Logo Cropper */}
      {selectedLogoFile && (
        <div className={styles.cropperOverlay} onClick={() => { setSelectedLogoFile(null); setCrop({ x: 0, y: 0 }); setZoom(1); }}>
          <div className={styles.cropperContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.cropContainer}>
              <Cropper
                image={selectedLogoFile}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className={styles.cropControls}>
              <div className={styles.zoomControl}>
                <label>Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
                <span>{Math.round(zoom * 100)}%</span>
              </div>
              <div className={styles.cropActions}>
                <button onClick={() => { setSelectedLogoFile(null); setCrop({ x: 0, y: 0 }); setZoom(1); }}>Cancel</button>
                <button onClick={handleSaveLogo}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cover Cropper */}
      {selectedCoverFile && (
        <div className={styles.cropperOverlay} onClick={() => { setSelectedCoverFile(null); setCrop({ x: 0, y: 0 }); setZoom(1); }}>
          <div className={styles.cropperContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.cropContainer}>
              <Cropper
                image={selectedCoverFile}
                crop={crop}
                zoom={zoom}
                aspect={31/10}
                cropShape="rect"
                showGrid={true}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className={styles.cropControls}>
              <div className={styles.zoomControl}>
                <label>Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
                <span>{Math.round(zoom * 100)}%</span>
              </div>
              <div className={styles.cropActions}>
                <button onClick={() => { setSelectedCoverFile(null); setCrop({ x: 0, y: 0 }); setZoom(1); }}>Cancel</button>
                <button onClick={handleSaveCover}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KennelClub;
